import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Block } from '@hvcgroep/design-system';

import './style.scss';
import { APP_ID } from '../../../config/constants';
import gtm from '../../../helpers/gtm';
import { AddressQueryMapper } from '../types';

const toUpperCase = (str: string | number) => {
  return String(str).charAt(0).toUpperCase() + String(str).slice(1).toLowerCase();
};

interface Props<AddressType> {
  address: AddressType
  addressMapper: Omit<AddressQueryMapper<NonNullable<AddressType | null>>, 'uid'>
  label?: string,
  variant?: 'simple' | 'full',
  onAddressReset?: () => void
}

const AddressSelectionInfo = <
  AddressType extends {
    [key in keyof AddressType]: string | number;
  },
>(props: Props<AddressType>) => {
  const {
    address,
    variant = 'full',
    onAddressReset,
    label,
    addressMapper,
  } = props;

  const { t } = useTranslation();

  if (variant === 'simple') {
    return (
      <div className="AddressSelectionInfo__address text-bold text-color text-italic">
        <div>{address[addressMapper.street]} {address[addressMapper.house_number]}{address[addressMapper.house_letter!] ? `-${address[addressMapper.house_letter!]}` : ''}{address[addressMapper.house_number_extension] ? `-${address[addressMapper.house_number_extension]}` : ''}<span className="AddressSelectionInfo__address__comma-after-housenumber">,&nbsp;</span></div>
        <div>{address[addressMapper.postal_code]}, {toUpperCase(address[addressMapper.city])}</div>
      </div>
    );
  }

  return (
    <div className="AddressSelectionInfo">
      <p className="text-bold mb-2">{label || t('address_selection.info.label')}</p>
      <Block tightLineHeight>
        <div className="AddressSelectionInfo__inner">
          <div className="AddressSelectionInfo__address">
            <div>{address[addressMapper.street]} {address[addressMapper.house_number]}{address[addressMapper.house_letter!] ? `-${address[addressMapper.house_letter!]}` : ''}{address[addressMapper.house_number_extension] ? `-${address[addressMapper.house_number_extension]}` : ''}<span className="AddressSelectionInfo__address__comma-after-housenumber">,&nbsp;</span></div>
            <div>{address[addressMapper.postal_code]}, {toUpperCase(address[addressMapper.city])}</div>
          </div>
          <div className="AddressSelectionInfo__edit-button">
            <button
              type="button"
              className="a no-styling"
              onClick={() => {
                // Callback function for address reset.
                if (onAddressReset) {
                  onAddressReset();
                }

                // Send an analytics event to GTM.
                gtm.trackUserEvent(String(APP_ID), 'Address reset', `${address[addressMapper.postal_code]}:${address[addressMapper.house_number]}${addressMapper.house_letter ? address[addressMapper.house_letter] : ''}${address[addressMapper.house_number_extension] || ''}}`);
              }}
            >
              {t('address_selection.info.change_address')}
            </button>
          </div>
        </div>
      </Block>
    </div>
  );
};

export default AddressSelectionInfo;
